// Katalog czesci pierwszej wersji.
//
// Konwencje lokalne (jednostki klockowe, os Y w gore):
//  - origin czesci = lewy-dolny naroznik obrysu (footprint)
//  - ksztalty kolizji: (x,y) = srodek wzgledem origin; w/h lub r w jednostkach klockowych
//  - sockety 'rigid' maja normalna (wersor powierzchni); 'axle' to os prostopadla do ekranu
//  - pozycje czesci na siatce moga byc wielokrotnoscia 0,5 (polowki dozwolone)

import type { PartDefinition, SocketDefinition } from './types';

/**
 * Sockety montazowe na wszystkich krawedziach czesci, co 1 kratke (jak studs
 * LEGO). Dzieki temu belka 6x1 ma 6 mocowan na gornej i 6 na dolnej krawedzi,
 * a nie tylko po jednym na srodku boku.
 *   t0..t{w-1}: gorna krawedz (normalna +y)
 *   b0..b{w-1}: dolna krawedz (normalna -y)
 *   l0..l{h-1}: lewa krawedz (normalna -x)
 *   r0..r{h-1}: prawa krawedz (normalna +x)
 */
function studSockets(w: number, h: number): SocketDefinition[] {
  const sockets: SocketDefinition[] = [];
  for (let i = 0; i < w; i += 1) {
    const x = 0.5 + i;
    sockets.push({ id: `t${i}`, kind: 'rigid', x, y: h, normal: { x: 0, y: 1 } });
    sockets.push({ id: `b${i}`, kind: 'rigid', x, y: 0, normal: { x: 0, y: -1 } });
  }
  for (let j = 0; j < h; j += 1) {
    const y = 0.5 + j;
    sockets.push({ id: `l${j}`, kind: 'rigid', x: 0, y, normal: { x: -1, y: 0 } });
    sockets.push({ id: `r${j}`, kind: 'rigid', x: w, y, normal: { x: 1, y: 0 } });
  }
  return sockets;
}

export const CATALOG: readonly PartDefinition[] = [
  {
    typeId: 'beam_1x1',
    label: 'Belka 1x1',
    category: 'frame',
    footprint: { w: 1, h: 1 },
    shapes: [{ kind: 'box', x: 0.5, y: 0.5, w: 1, h: 1 }],
    sockets: studSockets(1, 1),
    massKg: 0.15,
    appearance: { fill: '#5c7fa8', stroke: '#2c4055', socketColor: '#d7dee9' },
  },
  {
    typeId: 'beam_2x1',
    label: 'Belka 2x1',
    category: 'frame',
    footprint: { w: 2, h: 1 },
    shapes: [{ kind: 'box', x: 1, y: 0.5, w: 2, h: 1 }],
    sockets: studSockets(2, 1),
    massKg: 0.25,
    appearance: { fill: '#5c7fa8', stroke: '#2c4055', socketColor: '#d7dee9' },
  },
  {
    typeId: 'beam_4x1',
    label: 'Belka 4x1',
    category: 'frame',
    footprint: { w: 4, h: 1 },
    shapes: [{ kind: 'box', x: 2, y: 0.5, w: 4, h: 1 }],
    sockets: studSockets(4, 1),
    massKg: 0.3,
    appearance: { fill: '#5c7fa8', stroke: '#2c4055', socketColor: '#d7dee9' },
  },
  {
    typeId: 'axle_mount',
    label: 'Wspornik osi',
    category: 'mount',
    footprint: { w: 1, h: 1 },
    shapes: [{ kind: 'box', x: 0.5, y: 0.5, w: 1, h: 1 }],
    sockets: [
      { id: 'n', kind: 'rigid', x: 0.5, y: 1, normal: { x: 0, y: 1 } },
      // Os kola na dolnej krawedzi wspornika (na wysokosci spodu podwozia).
      { id: 'axle', kind: 'axle', x: 0.5, y: 0 },
      // Zapasowe mocowanie z boku - punkt zaczepienia dla dodatkowych klockow.
      { id: 'e', kind: 'rigid', x: 1, y: 0.5, normal: { x: 1, y: 0 } },
    ],
    massKg: 0.1,
    appearance: { fill: '#2f7d6c', stroke: '#16453b', socketColor: '#ffd479' },
  },
  {
    typeId: 'wheel_small',
    label: 'Kolo male',
    category: 'wheel',
    footprint: { w: 1, h: 1 },
    shapes: [{ kind: 'circle', x: 0.5, y: 0.5, r: 0.5 }],
    sockets: [{ id: 'axle', kind: 'axle', x: 0.5, y: 0.5 }],
    massKg: 0.25,
    appearance: { fill: '#2f3540', stroke: '#14171d', socketColor: '#ffd479' },
    wheel: { radiusGrid: 0.5, friction: 1.0 },
  },
  {
    typeId: 'wheel_large',
    label: 'Kolo duze',
    category: 'wheel',
    footprint: { w: 1, h: 1 },
    shapes: [{ kind: 'circle', x: 0.5, y: 0.5, r: 1.0 }],
    sockets: [{ id: 'axle', kind: 'axle', x: 0.5, y: 0.5 }],
    massKg: 0.5,
    appearance: { fill: '#3a4250', stroke: '#14171d', socketColor: '#ffd479' },
    wheel: { radiusGrid: 1.0, friction: 1.0 },
  },
  {
    typeId: 'battery',
    label: 'Akumulator',
    category: 'battery',
    footprint: { w: 1, h: 1 },
    shapes: [{ kind: 'box', x: 0.5, y: 0.5, w: 1, h: 1 }],
    sockets: [
      { id: 's', kind: 'rigid', x: 0.5, y: 0, normal: { x: 0, y: -1 } },
      // Gorne mocowanie: miejsce, by dokladac kolejne klocki.
      { id: 'n', kind: 'rigid', x: 0.5, y: 1, normal: { x: 0, y: 1 } },
    ],
    massKg: 1.2,
    appearance: { fill: '#3f7f5f', stroke: '#1e3d2e', socketColor: '#c9f5dd' },
  },
  {
    typeId: 'drive_unit',
    label: 'Modul napedu',
    category: 'drive',
    footprint: { w: 1, h: 1 },
    shapes: [{ kind: 'box', x: 0.5, y: 0.5, w: 1, h: 1 }],
    sockets: [
      // Mocowanie do korpusu (od gory). Wal wyjsciowy jest na DOLNEJ krawedzi,
      // dzieki czemu kolo mozna osadzic na nim WPROST (bez przekladni) - tak jak
      // na wsporniku osi - i kolo nie wchodzi w korpus nad silnikiem.
      { id: 'n', kind: 'rigid', x: 0.5, y: 1, normal: { x: 0, y: 1 } },
      { id: 'out', kind: 'axle', x: 0.5, y: 0 },
    ],
    massKg: 0.6,
    appearance: { fill: '#b4553f', stroke: '#5c2a20', socketColor: '#ffd7c9' },
    drive: { maxMotorTorque: 10.0, targetAngularVelocity: 9.0 },
  },
  {
    typeId: 'drive_fast',
    label: 'Naped szybki',
    category: 'drive',
    footprint: { w: 1, h: 1 },
    shapes: [{ kind: 'box', x: 0.5, y: 0.5, w: 1, h: 1 }],
    sockets: [
      { id: 'n', kind: 'rigid', x: 0.5, y: 1, normal: { x: 0, y: 1 } },
      { id: 'out', kind: 'axle', x: 0.5, y: 0 },
    ],
    massKg: 0.45,
    appearance: { fill: '#b07a2a', stroke: '#5d3f14', socketColor: '#ffe3ad' },
    drive: { maxMotorTorque: 5.0, targetAngularVelocity: 16.0 },
  },
  {
    typeId: 'drive_strong',
    label: 'Naped mocny',
    category: 'drive',
    footprint: { w: 1, h: 1 },
    shapes: [{ kind: 'box', x: 0.5, y: 0.5, w: 1, h: 1 }],
    sockets: [
      { id: 'n', kind: 'rigid', x: 0.5, y: 1, normal: { x: 0, y: 1 } },
      { id: 'out', kind: 'axle', x: 0.5, y: 0 },
    ],
    massKg: 0.95,
    appearance: { fill: '#8c3557', stroke: '#4a1a2c', socketColor: '#ffd2e2' },
    drive: { maxMotorTorque: 18.0, targetAngularVelocity: 5.0 },
  },
  {
    typeId: 'gearbox_reduce',
    label: 'Przekladnia redukcyjna',
    category: 'gear',
    footprint: { w: 2, h: 1 },
    shapes: [{ kind: 'box', x: 1, y: 0.5, w: 2, h: 1 }],
    sockets: [
      // Dwa waly sprzezone w obudowie. 'in' laczy sie z walem napedowym,
      // 'out' niesie kolo. Przelorzenie 2,5: wolniej, ale mocniej.
      { id: 'in', kind: 'axle', x: 0.5, y: 0.5 },
      { id: 'out', kind: 'axle', x: 1.5, y: 0.5 },
      { id: 's', kind: 'rigid', x: 1, y: 0, normal: { x: 0, y: -1 } },
      { id: 'n', kind: 'rigid', x: 1, y: 1, normal: { x: 0, y: 1 } },
    ],
    massKg: 0.35,
    appearance: { fill: '#3f6f96', stroke: '#1d3a52', socketColor: '#cfe8ff' },
    coupling: { fromSocketId: 'in', toSocketId: 'out', ratio: 2.5, kind: 'chain' },
  },
  {
    typeId: 'gearbox_speed',
    label: 'Przekladnia przyspieszajaca',
    category: 'gear',
    footprint: { w: 2, h: 1 },
    shapes: [{ kind: 'box', x: 1, y: 0.5, w: 2, h: 1 }],
    sockets: [
      { id: 'in', kind: 'axle', x: 0.5, y: 0.5 },
      { id: 'out', kind: 'axle', x: 1.5, y: 0.5 },
      { id: 's', kind: 'rigid', x: 1, y: 0, normal: { x: 0, y: -1 } },
      { id: 'n', kind: 'rigid', x: 1, y: 1, normal: { x: 0, y: 1 } },
    ],
    massKg: 0.3,
    appearance: { fill: '#5f8f6a', stroke: '#2b4632', socketColor: '#d8f5de' },
    coupling: { fromSocketId: 'in', toSocketId: 'out', ratio: 0.4, kind: 'chain' },
  },
  // --- Przekladnie: zebatki i sprockety do prawdziwych napedow ---
  // Promien podzialowy decyduje o zazebieniu: dwa kola siegaja sie, gdy
  // odleglosc osi = r1 + r2. Na siatce 0,5 daje to pary (0,5+1,0), (0,75+0,75) itd.
  {
    typeId: 'gear_8',
    label: 'Zebatka 8 zebow',
    category: 'gear',
    footprint: { w: 1, h: 1 },
    shapes: [{ kind: 'circle', x: 0.5, y: 0.5, r: 0.5 }],
    sockets: [{ id: 'axle', kind: 'axle', x: 0.5, y: 0.5 }],
    massKg: 0.15,
    appearance: { fill: '#c9a24a', stroke: '#5a4423', socketColor: '#ffd479', pattern: 'gear' },
    rotor: { kind: 'gear', pitchRadiusGrid: 0.5, teeth: 8 },
  },
  {
    typeId: 'gear_12',
    label: 'Zebatka 12 zebow',
    category: 'gear',
    footprint: { w: 1, h: 1 },
    shapes: [{ kind: 'circle', x: 0.5, y: 0.5, r: 0.75 }],
    sockets: [{ id: 'axle', kind: 'axle', x: 0.5, y: 0.5 }],
    massKg: 0.2,
    appearance: { fill: '#b8913f', stroke: '#4d3a1c', socketColor: '#ffd479', pattern: 'gear' },
    rotor: { kind: 'gear', pitchRadiusGrid: 0.75, teeth: 12 },
  },
  {
    typeId: 'gear_16',
    label: 'Zebatka 16 zebow',
    category: 'gear',
    footprint: { w: 1, h: 1 },
    shapes: [{ kind: 'circle', x: 0.5, y: 0.5, r: 1.0 }],
    sockets: [{ id: 'axle', kind: 'axle', x: 0.5, y: 0.5 }],
    massKg: 0.25,
    appearance: { fill: '#a9853a', stroke: '#463521', socketColor: '#ffd479', pattern: 'gear' },
    rotor: { kind: 'gear', pitchRadiusGrid: 1.0, teeth: 16 },
  },
  {
    typeId: 'sprocket_8',
    label: 'Sprocket 8 zebow',
    category: 'gear',
    footprint: { w: 1, h: 1 },
    shapes: [{ kind: 'circle', x: 0.5, y: 0.5, r: 0.5 }],
    sockets: [{ id: 'axle', kind: 'axle', x: 0.5, y: 0.5 }],
    massKg: 0.12,
    appearance: { fill: '#b08a4a', stroke: '#5a4423', socketColor: '#ffe3ad', pattern: 'gear' },
    rotor: { kind: 'sprocket', pitchRadiusGrid: 0.5, teeth: 8 },
  },
  {
    typeId: 'sprocket_12',
    label: 'Sprocket 12 zebow',
    category: 'gear',
    footprint: { w: 1, h: 1 },
    shapes: [{ kind: 'circle', x: 0.5, y: 0.5, r: 0.75 }],
    sockets: [{ id: 'axle', kind: 'axle', x: 0.5, y: 0.5 }],
    massKg: 0.18,
    appearance: { fill: '#a07c40', stroke: '#4d3a1c', socketColor: '#ffe3ad', pattern: 'gear' },
    rotor: { kind: 'sprocket', pitchRadiusGrid: 0.75, teeth: 12 },
  },
  {
    typeId: 'chain_drive',
    label: 'Przekladnia lancuchowa',
    category: 'mount',
    footprint: { w: 2, h: 1 },
    shapes: [{ kind: 'box', x: 1, y: 0.5, w: 2, h: 1 }],
    sockets: [
      // Dwa waly w jednej obudowie sprzezone LANCUCHEM. W przeciwienstwie do
      // zazebienia zebat (mesh) lancuch NIE odwraca kierunku obrotu.
      { id: 'in', kind: 'axle', x: 0.5, y: 0.5 },
      { id: 'out', kind: 'axle', x: 1.5, y: 0.5 },
      { id: 's', kind: 'rigid', x: 1, y: 0, normal: { x: 0, y: -1 } },
      { id: 'n', kind: 'rigid', x: 1, y: 1, normal: { x: 0, y: 1 } },
    ],
    massKg: 0.45,
    appearance: { fill: '#7a6a4a', stroke: '#3f3524', socketColor: '#ffe3ad', pattern: 'gear' },
    coupling: { fromSocketId: 'in', toSocketId: 'out', ratio: 2, kind: 'chain' },
  },
  // --- Szerokosc i struktura ---
  {
    typeId: 'beam_3x1',
    label: 'Belka 3x1',
    category: 'frame',
    footprint: { w: 3, h: 1 },
    shapes: [{ kind: 'box', x: 1.5, y: 0.5, w: 3, h: 1 }],
    sockets: studSockets(3, 1),
    massKg: 0.28,
    appearance: { fill: '#5c7fa8', stroke: '#2c4055', socketColor: '#d7dee9' },
  },
  {
    typeId: 'beam_6x1',
    label: 'Belka 6x1',
    category: 'frame',
    footprint: { w: 6, h: 1 },
    shapes: [{ kind: 'box', x: 3, y: 0.5, w: 6, h: 1 }],
    sockets: studSockets(6, 1),
    massKg: 0.75,
    appearance: { fill: '#5c7fa8', stroke: '#2c4055', socketColor: '#d7dee9' },
  },
  {
    typeId: 'beam_1x2',
    label: 'Belka pionowa 1x2',
    category: 'frame',
    footprint: { w: 1, h: 2 },
    shapes: [{ kind: 'box', x: 0.5, y: 1, w: 1, h: 2 }],
    sockets: studSockets(1, 2),
    massKg: 0.25,
    appearance: { fill: '#5c7fa8', stroke: '#2c4055', socketColor: '#d7dee9' },
  },
  {
    typeId: 'plate_2x2',
    label: 'Plyta 2x2',
    category: 'frame',
    footprint: { w: 2, h: 2 },
    shapes: [{ kind: 'box', x: 1, y: 1, w: 2, h: 2 }],
    sockets: studSockets(2, 2),
    massKg: 0.4,
    appearance: { fill: '#6a8cb4', stroke: '#33475c', socketColor: '#d7dee9' },
  },
  {
    typeId: 'bracket_L',
    label: 'Katownik',
    category: 'frame',
    footprint: { w: 2, h: 2 },
    // Katownik to dwa prostokaty: pionowe ramie i poziome ramie.
    shapes: [
      { kind: 'box', x: 0.5, y: 1, w: 1, h: 2 },
      { kind: 'box', x: 1.5, y: 0.5, w: 1, h: 1 },
    ],
    sockets: [
      { id: 's', kind: 'rigid', x: 0.5, y: 0, normal: { x: 0, y: -1 } },
      { id: 'n', kind: 'rigid', x: 0.5, y: 2, normal: { x: 0, y: 1 } },
      { id: 'w', kind: 'rigid', x: 0, y: 1, normal: { x: -1, y: 0 } },
      { id: 'e', kind: 'rigid', x: 2, y: 0.5, normal: { x: 1, y: 0 } },
    ],
    massKg: 0.35,
    appearance: { fill: '#5c7fa8', stroke: '#2c4055', socketColor: '#d7dee9' },
  },
  // --- Ciezar: najprostszy sposob zmiany srodka masy ---
  {
    typeId: 'ballast_light',
    label: 'Obciaznik lekki',
    category: 'frame',
    footprint: { w: 1, h: 1 },
    shapes: [{ kind: 'box', x: 0.5, y: 0.5, w: 1, h: 1 }],
    sockets: [
      { id: 's', kind: 'rigid', x: 0.5, y: 0, normal: { x: 0, y: -1 } },
      { id: 'n', kind: 'rigid', x: 0.5, y: 1, normal: { x: 0, y: 1 } },
    ],
    massKg: 0.6,
    appearance: { fill: '#6b6f78', stroke: '#33363d', socketColor: '#e6e9ee', pattern: 'ballast' },
  },
  {
    typeId: 'ballast_heavy',
    label: 'Obciaznik ciezki',
    category: 'frame',
    footprint: { w: 1, h: 1 },
    shapes: [{ kind: 'box', x: 0.5, y: 0.5, w: 1, h: 1 }],
    sockets: [
      { id: 's', kind: 'rigid', x: 0.5, y: 0, normal: { x: 0, y: -1 } },
      { id: 'n', kind: 'rigid', x: 0.5, y: 1, normal: { x: 0, y: 1 } },
    ],
    massKg: 2.5,
    appearance: { fill: '#3d4149', stroke: '#1d1f24', socketColor: '#c9ccd2', pattern: 'ballast' },
  },
  // --- Rozne kola ---
  {
    typeId: 'wheel_medium',
    label: 'Kolo srednie',
    category: 'wheel',
    footprint: { w: 1, h: 1 },
    shapes: [{ kind: 'circle', x: 0.5, y: 0.5, r: 0.75 }],
    sockets: [{ id: 'axle', kind: 'axle', x: 0.5, y: 0.5 }],
    massKg: 0.35,
    appearance: { fill: '#343b47', stroke: '#14171d', socketColor: '#ffd479' },
    wheel: { radiusGrid: 0.75, friction: 1.0 },
  },
  {
    typeId: 'wheel_huge',
    label: 'Kolo ogromne',
    category: 'wheel',
    footprint: { w: 1, h: 1 },
    shapes: [{ kind: 'circle', x: 0.5, y: 0.5, r: 1.5 }],
    sockets: [{ id: 'axle', kind: 'axle', x: 0.5, y: 0.5 }],
    massKg: 0.9,
    appearance: { fill: '#454e5e', stroke: '#14171d', socketColor: '#ffd479' },
    wheel: { radiusGrid: 1.5, friction: 1.0 },
  },
];

const BY_ID = new Map(CATALOG.map((def) => [def.typeId, def] as const));

// Zduplikowany typeId po cichu zwinie sie w Map (zostanie ostatni), a paleta
// pokaze dwa takie same przyciski. Lepiej zatrzymac sie od razu z komunikatem.
if (BY_ID.size !== CATALOG.length) {
  const ids = CATALOG.map((def) => def.typeId);
  const duplicated = [...new Set(ids.filter((id, index) => ids.indexOf(id) !== index))];
  throw new Error(`Zduplikowane typeId w katalogu: ${duplicated.join(', ')}`);
}

export function getDefinition(typeId: string): PartDefinition {
  const def = BY_ID.get(typeId);
  if (!def) throw new Error(`Nieznany typ czesci: ${typeId}`);
  return def;
}

export function hasDefinition(typeId: string): boolean {
  return BY_ID.has(typeId);
}

export function getSocket(def: PartDefinition, socketId: string) {
  const socket = def.sockets.find((s) => s.id === socketId);
  if (!socket) throw new Error(`Socket "${socketId}" nie istnieje w czesci ${def.typeId}`);
  return socket;
}
